import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { PageHeader, PageContainer } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { usageSummary } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { Gauge, Clock, Lightning, Warning, ShieldCheck, ArrowUpRight } from "@phosphor-icons/react";

const STATUS_LABEL = {
  trialing: "Trial", active: "Active", expired: "Trial ended",
  past_due: "Payment issue", suspended: "Suspended", canceled: "Canceled",
};

function barColor(pct) {
  if (pct >= 100) return "bg-red-500";
  if (pct >= 75) return "bg-amber-500";
  return "bg-[#FF5E00]";
}

function FeatureBar({ f }) {
  const unlimited = f.limit < 0;
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid={`usage-feature-${f.feature}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-sm font-medium capitalize">{f.label}</div>
        <div className="text-xs text-[#9a9a9a]" data-testid={`usage-count-${f.feature}`}>
          {unlimited ? `${f.used} used · Unlimited` : `${f.used} / ${f.limit}`}
        </div>
      </div>
      {!unlimited && (
        <>
          <div className="h-2 rounded-full bg-white/10 overflow-hidden">
            <div className={`h-full rounded-full transition-all ${barColor(f.pct)}`} style={{ width: `${Math.min(100, f.pct)}%` }} />
          </div>
          <div className="flex items-center justify-between mt-1.5">
            <span className="text-[11px] text-[#9a9a9a]">{f.remaining} remaining</span>
            {f.pct >= 100 ? (
              <span className="text-[11px] text-red-400 font-medium" data-testid={`usage-limit-reached-${f.feature}`}>Allowance used</span>
            ) : f.pct >= 75 ? (
              <span className="text-[11px] text-amber-400 font-medium">Approaching limit</span>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}

export default function BillingUsagePage() {
  const { activeId, activeClient } = useClient();
  const q = useQuery({
    queryKey: ["usage-summary", activeId],
    queryFn: () => usageSummary(activeId),
    enabled: !!activeId,
    refetchInterval: 60000,
  });
  const d = q.data;
  const features = d?.features || [];
  const nearLimit = features.some((f) => f.limit >= 0 && f.pct >= 75);
  const daysLeft = d?.trial_days_left;
  const showTrialWarning = d?.billing_status === "trialing" && daysLeft != null && daysLeft <= 7 && !d?.expired;

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Workspace"
        title="Billing & Usage"
        subtitle={`Your plan, trial status and monthly AI usage for ${activeClient?.name || "this workspace"}.`}
      >
        <Button asChild data-testid="usage-upgrade-btn" className="bg-[#FF5E00] hover:bg-[#ff7a2b] text-black font-semibold">
          <Link to="/billing">View plans <ArrowUpRight size={16} className="ml-1" /></Link>
        </Button>
      </PageHeader>

      {q.isLoading && <div className="text-sm text-[#9a9a9a] ai-thinking" data-testid="usage-loading">Loading usage…</div>}

      {d && (
        <div className="space-y-5">
          {/* Alerts */}
          {d.expired && (
            <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-4 flex items-start gap-3" data-testid="usage-trial-expired-banner">
              <Warning size={20} className="text-red-400 mt-0.5 shrink-0" />
              <div className="text-sm">
                <div className="font-semibold text-red-300">Your trial has ended — AI generation is paused</div>
                <div className="text-[#c9c9c9] mt-1">All of your campaigns, assets and history are safe. Choose a plan or contact support to continue using AI features.</div>
              </div>
              <Button asChild size="sm" className="ml-auto shrink-0 bg-red-500 hover:bg-red-400 text-black font-semibold">
                <Link to="/billing">Choose a plan</Link>
              </Button>
            </div>
          )}
          {!d.expired && showTrialWarning && (
            <div className={`rounded-xl border p-4 flex items-start gap-3 ${daysLeft <= 3 ? "border-red-500/40 bg-red-500/10" : "border-amber-500/40 bg-amber-500/10"}`} data-testid="usage-trial-warning-banner">
              <Clock size={20} className={`${daysLeft <= 3 ? "text-red-400" : "text-amber-400"} mt-0.5 shrink-0`} />
              <div className="text-sm">
                <div className={`font-semibold ${daysLeft <= 3 ? "text-red-300" : "text-amber-300"}`}>
                  Your trial ends in {daysLeft} day{daysLeft === 1 ? "" : "s"}
                </div>
                <div className="text-[#c9c9c9] mt-1">Upgrade now so your AI marketing team keeps working without interruption.</div>
              </div>
              <Button asChild size="sm" className="ml-auto shrink-0 bg-[#FF5E00] hover:bg-[#ff7a2b] text-black font-semibold">
                <Link to="/billing">Upgrade</Link>
              </Button>
            </div>
          )}
          {d.ai_suspended && (
            <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-300" data-testid="usage-suspended-banner">
              AI features are temporarily unavailable for this workspace. Please contact support for assistance.
            </div>
          )}
          {d.past_due_in_grace && (
            <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 flex items-start gap-3" data-testid="usage-grace-banner">
              <Warning size={20} className="text-amber-400 mt-0.5 shrink-0" />
              <div className="text-sm">
                <div className="font-semibold text-amber-300">There's a problem with your last payment</div>
                <div className="text-[#c9c9c9] mt-1">
                  Your AI features stay available until {String(d.grace_until || "").slice(0, 10)} while we retry the payment. Please update your billing details to avoid interruption.
                </div>
              </div>
              <Button asChild size="sm" className="ml-auto shrink-0 bg-amber-500 hover:bg-amber-400 text-black font-semibold">
                <Link to="/billing">Update billing</Link>
              </Button>
            </div>
          )}

          {/* Plan card */}
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5" data-testid="usage-plan-card">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#FF5E00]/15 flex items-center justify-center">
                  <ShieldCheck size={20} className="text-[#FF5E00]" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-[#9a9a9a]">Current plan</div>
                  <div className="font-head text-lg font-semibold" data-testid="usage-plan-name">{d.plan_name}</div>
                </div>
              </div>
              <Badge variant="outline" className={`border-white/20 ${d.billing_status === "active" ? "text-emerald-400" : d.expired ? "text-red-400" : "text-amber-300"}`} data-testid="usage-billing-status">
                {STATUS_LABEL[d.billing_status] || d.billing_status}
              </Badge>
              {d.billing_status === "trialing" && daysLeft != null && !d.expired && (
                <div className="text-sm text-[#c9c9c9] flex items-center gap-1.5" data-testid="usage-trial-days">
                  <Clock size={15} className="text-[#9a9a9a]" /> {daysLeft} day{daysLeft === 1 ? "" : "s"} left in trial
                </div>
              )}
              <div className="text-sm text-[#c9c9c9] flex items-center gap-1.5" data-testid="usage-reset-date">
                <Lightning size={15} className="text-[#9a9a9a]" /> Usage resets on {d.next_reset}
              </div>
              {d.price_monthly > 0 && <div className="text-sm text-[#9a9a9a]">${d.price_monthly}/mo</div>}
            </div>
          </div>

          {nearLimit && !d.expired && (
            <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-200 flex items-center gap-2" data-testid="usage-near-limit-banner">
              <Warning size={17} className="shrink-0" />
              You're approaching your monthly AI allowance on one or more features. Upgrade your plan for more usage.
              <Button asChild size="sm" variant="outline" className="ml-auto shrink-0 border-amber-500/40 text-amber-200 hover:bg-amber-500/10">
                <Link to="/billing">See plans</Link>
              </Button>
            </div>
          )}

          {/* Feature usage */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Gauge size={18} className="text-[#FF5E00]" />
              <h2 className="text-base md:text-lg font-semibold">AI usage this period</h2>
              <span className="text-xs text-[#9a9a9a]">({d.period_start} → {d.period_end})</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="usage-feature-grid">
              {features.map((f) => <FeatureBar key={f.feature} f={f} />)}
            </div>
          </div>
        </div>
      )}
    </PageContainer>
  );
}
